import { isUndefined } from './utils';
function getByPath(obj, path) {
    if (!path)
        return undefined;
    return path.split('.').reduce((acc, k) => (isUndefined(acc) ? acc : acc[k]), obj);
}
function pick(src, pathOrFn) {
    if (typeof pathOrFn === 'function') {
        return pathOrFn(src);
    }
    return getByPath(src, pathOrFn);
}
const applyMapping = (src, mapping) => {
    return src.map((row) => {
        const item = {};
        Object.keys(mapping).forEach((key) => {
            const mappingKey = key;
            const value = pick(row, mapping[mappingKey]);
            if (value !== undefined) {
                item[mappingKey] = value;
            }
        });
        return item;
    });
};
/**
 * Converts input data to diagram model format using the provided mapping configuration.
 *
 * This function takes raw input data and transforms it into a structured diagram model
 * containing shapes and connections arrays. It uses the mapping configuration to
 * extract data from the input and apply field mappings to create properly formatted
 * ShapeOptions and ConnectionOptions objects.
 *
 * @param input - The raw input data object containing source data for shapes and connections
 * @param mapping - Configuration object defining how to extract and map data from input
 * @param mapping.shapes - Shape mapping configuration with source path and field mappings
 * @param mapping.connections - Connection mapping configuration with source path and field mappings
 * @returns An object containing arrays of mapped shapes and connections ready for diagram rendering
 *
 * @example
 * ```typescript
 * const input = {
 *   items: [{ id: '1', name: 'Node 1', x: 100, y: 100 }],
 *   links: [{ from: '1', to: '2' }]
 * };
 *
 * const mapping = {
 *   shapes: {
 *     source: 'items',
 *     map: { id: 'id', content: 'name', x: 'x', y: 'y' }
 *   },
 *   connections: {
 *     source: 'links',
 *     map: { from: 'from', to: 'to' }
 *   }
 * };
 *
 * const result = convertToDiagramModel(input, mapping);
 * // Returns: { shapes: [...], connections: [...] }
 * ```
 *
 */
export const convertToDiagramModel = (input, mapping) => {
    const { source: shapesSourceCollection, map: shapesMapping } = mapping.shapes;
    const shapesData = pick(input, shapesSourceCollection) || [];
    const shapes = applyMapping(shapesData, shapesMapping);
    const { source: connectionsSourceCollection, map: connectionsMapping } = mapping.connections;
    const connectionsData = pick(input, connectionsSourceCollection) || [];
    const connections = applyMapping(connectionsData, connectionsMapping);
    return { shapes, connections };
};
